import { useEffect, useState } from 'react';
import { t, type MessageKey } from '../../i18n';
import {
  createGame,
  fetchLeaderboard,
  identityNotSaved,
  loadIdentity,
  onlineStatus,
  roomIdFrom,
  saveName,
  type Leaderboard,
  type NameError,
  type OnlineStatus,
} from '../../app/online';
import { ONLINE_PRESETS } from '../../online/protocol';
import { Choice, Field } from '../components/Form';

type Preset = (typeof ONLINE_PRESETS)[number];

/** Why a name cannot be used, as the player reads it (`taken`: the room said so). */
export function nameErrorText(error: NameError | 'taken'): string {
  switch (error) {
    case 'invalid':
      return t('online.nameInvalid');
    case 'scripts':
      return t('online.nameScripts');
    case 'reserved':
      return t('online.nameReserved');
    case 'offensive':
      return t('online.nameOffensive');
    case 'taken':
      return t('online.nameTaken');
  }
}

/** The online lobby: a name, then create a game to share, or join one by link or code. */
export function OnlineScreen({
  onBack,
  onOpenRoom,
  onLeaderboard,
}: {
  onBack: () => void;
  onOpenRoom: (id: string) => void;
  onLeaderboard: () => void;
}) {
  const [status, setStatus] = useState<OnlineStatus | null>(null);
  const [identity] = useState(loadIdentity);
  const [name, setName] = useState(identity.name);
  const [preset, setPreset] = useState<Preset>('A');
  const [join, setJoin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [board, setBoard] = useState<Leaderboard | null>(null);
  // Storage refused this browser's identity: say so before a game, not after a reload.
  const [notSaved, setNotSaved] = useState(identityNotSaved);

  useEffect(() => {
    let live = true;
    void onlineStatus().then((s) => {
      if (!live) return;
      setStatus(s);
      if (s.leaderboard) void fetchLeaderboard(identity.player).then((b) => live && setBoard(b));
    });
    return () => {
      live = false;
    };
  }, [identity.player]);

  const named = (): boolean => {
    const saved = saveName(name);
    const unsaved = identityNotSaved();
    setNotSaved(unsaved);
    if ('error' in saved) {
      setError(nameErrorText(saved.error));
      return false;
    }
    // Storage refused the identity just now: the player sees why first (pressing again goes on).
    return !unsaved || notSaved !== null;
  };

  const create = async () => {
    if (!named()) return;
    setBusy(true);
    setError(null);
    const r = await createGame(preset);
    setBusy(false);
    if ('id' in r) onOpenRoom(r.id);
    else setError(t('online.createFailed', { message: r.error }));
  };

  const joinGame = () => {
    if (!named()) return;
    const id = roomIdFrom(join);
    if (id) onOpenRoom(id);
    else setError(t('online.joinInvalid'));
  };

  const available = status?.online === true;
  return (
    <main className="page page-online">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onBack}>
          ← {t('menu.back')}
        </button>
        <h1>{t('online.title')}</h1>
      </header>
      <p className="lead">{t('online.intro')}</p>
      {status === null && <p className="dim">{t('online.checking')}</p>}
      {status && !available && (
        <p className="notice notice-error" role="alert">
          {t('online.unavailable')}
        </p>
      )}
      {error && (
        <p className="notice notice-error" role="alert">
          {error}
        </p>
      )}
      {notSaved && (
        <p className="notice notice-error" role="alert">
          {t('online.identityNotSaved', { message: notSaved })}
        </p>
      )}
      <section className="panel online-panel">
        <Field label={t('online.name')} hint={t('online.nameHint')}>
          <input
            type="text"
            value={name}
            maxLength={24}
            autoComplete="nickname"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Choice
          legend={t('online.preset')}
          value={preset}
          onChange={setPreset}
          options={ONLINE_PRESETS.map((p) => ({
            value: p,
            label: t(`setup.preset.${p}` as MessageKey),
            hint: t(`setup.preset.${p}.hint` as MessageKey),
          }))}
        />
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary btn-large"
            disabled={!available || busy}
            onClick={() => void create()}
          >
            {busy ? t('online.creating') : t('online.create')}
          </button>
        </div>
      </section>
      <section className="panel online-panel">
        <h2>{t('online.join')}</h2>
        <Field label={t('online.joinLabel')} hint={t('online.joinHint')}>
          <input
            type="text"
            value={join}
            inputMode="url"
            autoCapitalize="off"
            spellCheck={false}
            onChange={(e) => setJoin(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && joinGame()}
          />
        </Field>
        <div className="form-actions">
          <button type="button" className="btn" disabled={!available} onClick={joinGame}>
            {t('online.joinButton')}
          </button>
        </div>
      </section>
      {status?.leaderboard && (
        <section className="panel online-panel">
          <h2>{t('leaderboard.title')}</h2>
          <p className="dim">{t('online.rated')}</p>
          <p>
            {board?.me
              ? t('online.yourRating', {
                  rating: board.me.rating,
                  rank: board.me.rank,
                  wins: board.me.wins,
                  losses: board.me.losses,
                  draws: board.me.draws,
                })
              : t('online.unrated')}
          </p>
          <button type="button" className="btn" onClick={onLeaderboard}>
            {t('menu.leaderboard')}
          </button>
        </section>
      )}
    </main>
  );
}
